import * as React from 'react';
import {
  GoogleMap,
  LoadScript,
  DirectionsService,
  DirectionsRenderer,
  OverlayViewF,
  Marker,
} from '@react-google-maps/api';
import warehouseIcon from 'assets/images/marker-warehouse.png';
import truckIcon from 'assets/images/markerTruck.png';
import clientIcon from 'assets/images/marker-client.png';
import type {
  InvoiceTravelInformation,
} from 'contracts/tracking-v2/invoice-travel-information';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';

import { useInvoiceTransportMapHelper } from './hooks';
import {
  OriginPositionCard,
  type OriginPositionCardRef,
  DestinationPositionCard,
  type DestinationPositionCardRef,
  TruckLastPositionCard,
  type TruckLastPositionCardRef,
} from './components';

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY_SINERGIA as string;
const MAP_ID = '87412a572f003751';

type Props = React.ComponentProps<'div'> & {
  onInvoiceTravelInformationChange?: (info: InvoiceTravelInformation) => void;
}

export const InvoiceTransportMap = ({
                                      onInvoiceTravelInformationChange,
                                      className,
                                      ...props
                                    }: Props) => {
  const {
    destinationPosition,
    handleOriginAndDestinationDirectionsServiceResponse,
    handleTruckDirectionsServiceResponse,
    originAndDestinationDirectionOptions,
    originAndDestinationDirections,
    originPosition,
    truckDirectionOptions,
    truckDirections,
    lastTruckPosition,
    truckAndDestinationOptions,
    handleTruckAndDestinationDirectionsServiceResponse,
    mapDataIsReady,
    invoiceTravelInformation,
  } = useInvoiceTransportMapHelper();

  const originPositionCardRef = React.useRef<OriginPositionCardRef>(null);
  const destinationPositionCardRef = React.useRef<DestinationPositionCardRef>(null);
  const truckLastPositionCardRef = React.useRef<TruckLastPositionCardRef>(null);

  const notifyInvoiceTravelInformationUpdate = React.useCallback(() => {
    if (invoiceTravelInformation) {
      onInvoiceTravelInformationChange?.(invoiceTravelInformation);
    }
  }, [invoiceTravelInformation, onInvoiceTravelInformationChange]);

  React.useEffect(() => {
    notifyInvoiceTravelInformationUpdate();
  }, [notifyInvoiceTravelInformationUpdate]);

  if (!mapDataIsReady) {
    return (
      <Skeleton {...props} className={cn('w-full h-[540px] rounded-md overflow-hidden', className)} />
    );
  }

  return (
    <div {...props} className={cn('w-full h-[540px] rounded-md overflow-hidden', className)}>
      <LoadScript googleMapsApiKey={API_KEY} mapIds={[MAP_ID]}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          zoom={10}
          clickableIcons={false}
          options={{ mapId: MAP_ID, gestureHandling: 'cooperative' }}
        >
          {originAndDestinationDirectionOptions && (
            <DirectionsService
              options={originAndDestinationDirectionOptions}
              callback={handleOriginAndDestinationDirectionsServiceResponse}
            />
          )}
          {originAndDestinationDirections && (
            <DirectionsRenderer options={{
              directions: originAndDestinationDirections,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#FFC100',
                strokeWeight: 8,
                strokeOpacity: 0.5,
              },
            }} />
          )}
          {originPosition && (
            <OverlayViewF mapPaneName={'floatPane'} position={originPosition}>
              <OriginPositionCard ref={originPositionCardRef} />
            </OverlayViewF>
          )}
          {destinationPosition && (
            <OverlayViewF mapPaneName={'floatPane'} position={destinationPosition}>
              <DestinationPositionCard ref={destinationPositionCardRef} />
            </OverlayViewF>
          )}
          {truckDirectionOptions && (
            <DirectionsService
              options={truckDirectionOptions}
              callback={handleTruckDirectionsServiceResponse}
            />
          )}
          {truckAndDestinationOptions && (
            <DirectionsService
              options={truckAndDestinationOptions}
              callback={handleTruckAndDestinationDirectionsServiceResponse}
            />
          )}
          {truckDirections && (
            <DirectionsRenderer options={{
              directions: truckDirections,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#FF4D00',
                strokeWeight: 5,
                strokeOpacity: 0.7,
              },
            }} />
          )}
          {lastTruckPosition && (
            <OverlayViewF mapPaneName={'floatPane'} position={lastTruckPosition}>
              <TruckLastPositionCard ref={truckLastPositionCardRef} />
            </OverlayViewF>
          )}
          {originPosition && (
            <Marker
              position={originPosition}
              icon={warehouseIcon}
              onClick={() => originPositionCardRef.current?.toggleOpen()}
            />
          )}
          {destinationPosition && (
            <Marker
              position={destinationPosition}
              icon={clientIcon}
              onClick={() => destinationPositionCardRef.current?.toggleOpen()}
            />
          )}
          {lastTruckPosition && (
            <Marker
              position={lastTruckPosition}
              icon={truckIcon}
              onClick={() => truckLastPositionCardRef.current?.toggleOpen()}
            />
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};